import React, { useEffect, useId, useRef, useState } from 'react';
import { dayjs } from '@utils';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
import { StyledRangePicker } from './styled';

export const RangePicker = ({ onChange = () => null, popupClassName, ...props }) => {
    const [open, setOpen] = useState(false);
    const popupRef = useRef(null);
    // Unique class to find this picker's popup: all popups are rendered into the first .range-picker
    const instancePopupClassName = `range-picker-popup-${useId().replace(/:/g, '')}`;

    const handleChange = (result) => {
        return onChange({
            min: result?.[0].startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
            max: result?.[1].endOf('day').format('YYYY-MM-DDTHH:mm:ss')
        });
    };

    useEffect(() => {
        document.querySelectorAll('.ant-picker-input input').forEach((element, index) => {
            element.setAttribute('data-e2e', `datepicker_${index === 0 ? 'start' : 'end'}-date_input`);
        });

        if (open) {
            const popup = document.querySelector(`.${instancePopupClassName}`);
            popupRef.current = popup;
            if (popup) {
                const panels = popup.querySelectorAll('.ant-picker-panel');
                // Prev buttons are shown in the first panel and next buttons in the last one
                const lastOf = (selector) => [...popup.querySelectorAll(selector)].pop();

                popup.setAttribute('data-e2e', 'datepicker_popup');
                panels[0]?.setAttribute('data-e2e', 'datepicker_start-panel');
                if (panels.length > 1) panels[panels.length - 1].setAttribute('data-e2e', 'datepicker_end-panel');
                popup
                    .querySelector('.ant-picker-header-super-prev-btn')
                    ?.setAttribute('data-e2e', 'datepicker_prev-year_btn');
                popup
                    .querySelector('.ant-picker-header-prev-btn')
                    ?.setAttribute('data-e2e', 'datepicker_prev-month_btn');
                lastOf('.ant-picker-header-next-btn')?.setAttribute('data-e2e', 'datepicker_next-month_btn');
                lastOf('.ant-picker-header-super-next-btn')?.setAttribute('data-e2e', 'datepicker_next-year_btn');
            }
        } else if (popupRef.current) {
            // Closed popup stays in DOM, so only the opened one keeps the data-e2e attributes added above
            popupRef.current
                .querySelectorAll(
                    [
                        'datepicker_start-panel',
                        'datepicker_end-panel',
                        'datepicker_prev-year_btn',
                        'datepicker_prev-month_btn',
                        'datepicker_next-month_btn',
                        'datepicker_next-year_btn'
                    ]
                        .map((dataE2e) => `[data-e2e="${dataE2e}"]`)
                        .join(',')
                )
                .forEach((element) => element.removeAttribute('data-e2e'));
            popupRef.current.removeAttribute('data-e2e');
            popupRef.current = null;
        }
    }, [open]);

    return (
        <StyledRangePicker
            className='range-picker'
            getPopupContainer={() => document.querySelector('.range-picker')}
            allowClear={{ clearIcon: <Icon.CloseIcon width='24px' height='24px' data-e2e='datepicker_clear_icon' /> }}
            presets={[
                {
                    label: (
                        <Typography.Text type='badgeLarge' data-e2e='datepicker_quick-select_title'>
                            Quick select
                        </Typography.Text>
                    )
                },
                {
                    label: <span data-e2e='datepicker_last-7-days_select'>Last 7 days</span>,
                    value: [dayjs().subtract(7, 'd'), dayjs()]
                },
                {
                    label: <span data-e2e='datepicker_last-30-days_select'>Last 30 days</span>,
                    value: [dayjs().subtract(30, 'd'), dayjs()]
                },
                {
                    label: <span data-e2e='datepicker_week-to-date_select'>Week-to-date</span>,
                    value: [dayjs().weekday(1), dayjs()]
                },
                {
                    label: <span data-e2e='datepicker_month-to-date_select'>Month-to-date</span>,
                    value: [dayjs().startOf('month'), dayjs()]
                },
                {
                    label: <span data-e2e='datepicker_year-to-date_select'>Year-to-date</span>,
                    value: [dayjs().startOf('year'), dayjs()]
                }
            ]}
            suffixIcon={<Icon.DatePickerIcon width='24px' height='24px' data-e2e='datepicker_open_icon' />}
            separator=':'
            popupClassName={[popupClassName, instancePopupClassName].filter(Boolean).join(' ')}
            onOpenChange={setOpen}
            onChange={handleChange}
            {...props}
        />
    );
};
